import {
  useState, useMemo 
} from 'react';
import {
  useLocation, useNavigate 
} from 'react-router-dom';
import {
  RefreshCw, Camera 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  useWasteLogs, WasteAnalysisModal 
} from '@/features/waste';
import type {
  WasteReason, WasteAnalysisResult 
} from '@/features/waste';
import { toast } from 'sonner';
import {
  DEFAULT_SITE_ID, TABS, type TabId 
} from './waste-constants';
import { LogView } from './log-view';
import { ReportsView } from './reports-view';

export function WastePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedReason, setSelectedReason] = useState<WasteReason | undefined>(undefined);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);

  const {
    data, isLoading, error, refetch, isFetching 
  } = useWasteLogs(DEFAULT_SITE_ID, { reason: selectedReason });
  const { data: allData } = useWasteLogs(DEFAULT_SITE_ID);

  const activeTab = useMemo<TabId>(() => {
    if (location.pathname.includes('/reports')) return 'reports';
    return 'log';
  }, [location.pathname]);

  const wasteLogs = data?.data ?? [];

  const handleBulkUpload = (file: File) => {
    toast.success(`Processing ${file.name}...`);
    setTimeout(() => {
      toast.success('Imported waste logs');
      refetch();
    }, 1500);
  };

  const handleWasteAnalysis = (result: WasteAnalysisResult) => {
    // Log each identified waste item
    const itemCount = result.items.length;
    const pluralSuffix = itemCount === 1 ? '' : 's';
    toast.success(
      `AI identified ${itemCount} waste item${pluralSuffix} totaling ${result.totalEstimatedCost.toFixed(2)}`
    );
    // In a real implementation, this would create waste log entries
    refetch();
  };

  const stats = useMemo(() => {
    const logs = allData?.data ?? [];
    const preConsumer = logs.filter((w) => w.type === 'pre-consumer');
    const postConsumer = logs.filter((w) => w.type === 'post-consumer');
    const byReason: Record<string, number> = {};
    for (const log of logs) {
      byReason[log.reason] = (byReason[log.reason] ?? 0) + log.estimatedCost;
    }
    return {
      totalLogs: logs.length,
      totalCost: allData?.meta?.totalCost ?? 0,
      preConsumerCost: preConsumer.reduce((sum, w) => sum + w.estimatedCost, 0),
      postConsumerCost: postConsumer.reduce((sum, w) => sum + w.estimatedCost, 0),
      byReason,
    };
  }, [allData]);

  return (
    <div className="space-y-6">
      {/* AI Waste Analysis Modal */}
      <WasteAnalysisModal
        isOpen={showAnalysisModal}
        onClose={() => setShowAnalysisModal(false)}
        onAnalysisComplete={handleWasteAnalysis}
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Waste Tracking</h1>
          <p className="text-muted-foreground">Monitor and reduce food waste across venues</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAnalysisModal(true)}
            className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-200 hover:border-purple-300"
          >
            <Camera className="mr-2 h-4 w-4 text-purple-500" />
            AI Photo Log
          </Button>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <TabNavigation activeTab={activeTab} onNavigate={navigate} />

      {activeTab === 'reports' ? (
        <ReportsView stats={stats} />
      ) : (
        <LogView
          wasteLogs={wasteLogs}
          isLoading={isLoading}
          error={error}
          stats={stats}
          selectedReason={selectedReason}
          setSelectedReason={setSelectedReason}
          onBulkUpload={handleBulkUpload}
          onRefetch={refetch}
        />
      )}
    </div>
  );
}

interface TabNavigationProps {
  readonly activeTab: TabId;
  readonly onNavigate: (href: string) => void;
}

function TabNavigation({
  activeTab, onNavigate 
}: TabNavigationProps) {
  return (
    <div className="border-b">
      <nav className="flex gap-4">
        {TABS.map((tab) => {
          const TabIcon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onNavigate(tab.href)}
              className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                isActive
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground'
              }`}
            >
              <TabIcon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
